import { html, nothing, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import {
    isPending,
    SubmissionStatus,
    submissionStatusColor,
    submissionStatusIcon,
    submissionStatusLabel,
} from "@/submission_status";

/**
 * A panel listing a user's past submissions for an exercise.
 * Rows are links to the submission page, so modifier clicks open a new tab.
 * A plain click dispatches `submission-selected` with the submission id instead. It does not
 * highlight the row itself: d-handin-card sets `activeId` once it shows the submission, which it
 * may decline to do.
 * d-handin-card updates `submissions` directly: it adds a submission as soon as it is
 * handed in and replaces it with the judged version once evaluation finishes.
 * A submission that is still being judged has no result to show, so its row is inert.
 *
 * Styles: app/assets/stylesheets/components/handin.css.scss (.handin-card)
 *
 * @element d-submission-history
 *
 * @prop {SubmissionStatus[]} submissions - JSON-encoded array of submission objects
 * @prop {number|null} activeId - ID of the currently displayed submission (highlighted row)
 * @prop {string} baseUrl - URL prefix a submission id is appended to
 *
 * @fires submission-selected - When a row is clicked; detail: { id: number }
 */
@customElement("d-submission-history")
export class SubmissionHistory extends DodonaElement {
    @property({ type: Array })
    submissions: SubmissionStatus[] = [];

    @property({ type: Number, attribute: "active-id" })
    activeId: number | null = null;

    @property({ type: String, attribute: "base-url" })
    baseUrl = "";

    // Submissions are ordered most-recent-first, so the last submission is the first element.
    private get judged(): SubmissionStatus[] {
        return this.submissions.filter((s) => !isPending(s.status));
    }

    /** The `#N` shown for a submission, or null when it is not in this list. */
    numberFor(id: number | null): number | null {
        return this.submissions.find((s) => s.id === id)?.number ?? null;
    }

    get accepted(): boolean {
        return this.judged[0]?.accepted ?? false;
    }

    get solved(): boolean {
        return this.judged.some((s) => s.accepted);
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.closest(".dropdown")?.addEventListener("shown.bs.dropdown", this.revealActiveRow);
    }

    disconnectedCallback(): void {
        this.closest(".dropdown")?.removeEventListener("shown.bs.dropdown", this.revealActiveRow);
        super.disconnectedCallback();
    }

    /**
     * Scrolls the list itself rather than calling scrollIntoView, which would also scroll every
     * ancestor scroller up to the document and move the page out from under the reader.
     * Runs when the dropdown opens rather than when the active id changes: d-handin-card sets that id
     * on every view change, usually while the menu is closed and the list has no layout to scroll.
     * Re-running it on every render would also undo a manual scroll.
     */
    private revealActiveRow = (): void => {
        const list = this.querySelector<HTMLElement>(".submission-history-list");
        const active = list?.querySelector<HTMLElement>(".submission-history-row--active");
        if (!list || !active) return;

        // Offsets rather than bounding rects: the menu is still animating into place when this runs,
        // which shifts every rect but leaves the row's position inside the list untouched.
        const top = active.offsetTop - list.offsetTop;
        const bottom = top + active.offsetHeight;
        if (top < list.scrollTop) {
            list.scrollTop = top;
        } else if (bottom > list.scrollTop + list.clientHeight) {
            list.scrollTop = bottom - list.clientHeight;
        }
    };

    private onRowClick(event: MouseEvent, submission: SubmissionStatus): void {
        // Let the browser handle modifier clicks so the submission opens in a new tab or window.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }
        event.preventDefault();
        this.dispatchEvent(
            new CustomEvent("submission-selected", {
                detail: { id: submission.id },
                bubbles: true,
            }),
        );
    }

    private renderStatus(): TemplateResult {
        if (this.accepted) {
            return html`<span class="colored-correct">
                <i class="mdi mdi-check-circle-outline mdi-18" aria-hidden="true"></i>
                ${I18n.t("js.submission_history_solved")}
            </span>`;
        }
        if (this.solved) {
            return html`<span class="colored-warning">
                <i class="mdi mdi-alert-circle-outline mdi-18" aria-hidden="true"></i>
                ${I18n.t("js.submission_history_wrong_after_correct")}
            </span>`;
        }
        return html`<span class="colored-wrong">
            <i class="mdi mdi-close-circle-outline mdi-18" aria-hidden="true"></i>
            ${I18n.t("js.submission_history_not_solved")}
        </span>`;
    }

    render(): TemplateResult {
        return html`
            <div class="submission-history-header">
                <span class="submission-history-title">${I18n.t("js.submission_history")}</span>
                ${this.judged.length > 0 ? this.renderStatus() : nothing}
            </div>
            ${
                this.submissions.length > 0
                    ? html`<div class="card-row-list submission-history-list">
                          ${this.submissions.map((s) => this.renderRow(s))}
                      </div>`
                    : html`<div class="submission-history-empty">${I18n.t("js.submission_history_empty")}</div>`
            }
        `;
    }

    private renderRow(submission: SubmissionStatus): TemplateResult {
        const icon = submissionStatusIcon(submission.status);
        const color = submissionStatusColor(submission.status);
        const isActive = submission.id === this.activeId;
        const pending = isPending(submission.status);
        const label = submissionStatusLabel(submission.status);
        const number = submission.number ?? null;
        const title = number === null ? label : `#${number} · ${label}`;

        return html`
            <a
                class=${classMap({
                    "card-row": true,
                    "submission-history-row": true,
                    "submission-history-row--active": isActive,
                    "submission-history-row--pending": pending,
                })}
                href=${pending ? nothing : `${this.baseUrl}${submission.id}`}
                @click=${(e: MouseEvent) => (pending ? e.preventDefault() : this.onRowClick(e, submission))}
            >
                <span class="card-row-leading">
                    <i class="mdi mdi-${icon} colored-${color}" aria-hidden="true"></i>
                </span>
                <span class="card-row-content">
                    <span class="card-row-title colored-${color}">${title}</span>
                    ${submission.summary ? html`<span class="card-row-subtitle">${submission.summary}</span>` : nothing}
                </span>
                <span class="card-row-trailing">
                    <d-time-ago datetime="${submission.created_at}" class="card-row-timestamp" compact></d-time-ago>
                </span>
            </a>
        `;
    }
}
